import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { JSONContent } from "@tiptap/react";
import { FiSettings, FiArrowRight, FiFileText } from "react-icons/fi";

// Reusable Components from common/
import { PageHeader } from "../../components/common";

// Page-specific Components
import ComposeEditor from "../../components/compose/ComposeEditor";
import InsightsSidebar from "../../components/compose/InsightsSidebar";
import SuggestionsSidebar from "../../components/compose/SuggestionsSidebar";
import WorkflowConfigPanel from "../../components/compose/WorkflowConfigPanel";
import AIRevampModal from "../../components/compose/AIRevampModal";
import ChooseTemplateModal from "../../components/compose/ChooseTemplateModal";
import ChooseDraftModal from "../../components/compose/ChooseDraftModal";

// Types
import type { ContentSuggestion } from "../../types/suggestions";

// Services & Types
import { apiService } from "../../services/apiService";
import { integrationsService } from "../../services/integrationsService";
import { builtInTemplates } from "../../data/builtInTemplates";

// Local insight type for display
interface DisplayInsight {
  id: string;
  title: string;
  summary?: {
    headline?: string;
    narrative?: string;
  } | string;
  data_sources?: string[];
  created_at: string;
}

// Draft type for loading saved drafts
interface Draft {
  id: string;
  subject: string;
  content: JSONContent | null;
  workflow_config: any;
  selected_integrations: string[];
  created_at: string;
  updated_at: string;
}

// Styles
import "../../styles/compose.css";

// Type-safe icon wrappers
const SettingsIcon = FiSettings as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;

interface WorkflowConfig {
  name: string;
  contactListId: string;
  description: string;
  mode: "manual" | "automatic";
}

interface Integration {
  id: string;
  provider: string;
  name: string;
}

// Helper function to generate HTML from TipTap JSON (moved outside component)
const generateHTMLFromJSON = (json: JSONContent): string => {
  // Simple recursive function to convert TipTap JSON to HTML
  const nodeToHTML = (node: any): string => {
    if (node.type === 'text') {
      let text = node.text || '';
      if (node.marks) {
        node.marks.forEach((mark: any) => {
          if (mark.type === 'bold') text = `<strong>${text}</strong>`;
          if (mark.type === 'italic') text = `<em>${text}</em>`;
          if (mark.type === 'underline') text = `<u>${text}</u>`;
        });
      }
      return text;
    }
    
    const children = node.content ? node.content.map(nodeToHTML).join('') : '';
    
    switch (node.type) {
      case 'paragraph':
        return `<p>${children}</p>`;
      case 'heading':
        return `<h${node.attrs?.level || 1}>${children}</h${node.attrs?.level || 1}>`;
      case 'bulletList':
        return `<ul>${children}</ul>`;
      case 'orderedList':
        return `<ol>${children}</ol>`;
      case 'listItem':
        return `<li>${children}</li>`;
      case 'blockquote':
        return `<blockquote>${children}</blockquote>`;
      case 'doc':
        return children;
      default:
        return children;
    }
  };
  
  return nodeToHTML(json);
};

/**
 * ComposeUpdatePage - Main page for composing email updates
 * Layout: Header + optional config panel + two-column grid
 */
const ComposeUpdatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editorRef = useRef<{ insertAtCursor: (text: string) => void; setContent: (html: string) => void; getHTML: () => string; getText: () => string } | null>(null);

  // Editor state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState<JSONContent | null>(null);

  // Workflow config state
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [workflowConfig, setWorkflowConfig] = useState<WorkflowConfig>({
    name: "",
    contactListId: "",
    description: "",
    mode: "manual",
  });

  // Integration state
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([]);

  // Insight state
  const [generatedInsights, setGeneratedInsights] = useState<DisplayInsight[]>([]);
  const [pastInsights, setPastInsights] = useState<DisplayInsight[]>([]);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [selectedInsights, setSelectedInsights] = useState<DisplayInsight[]>([]);

  // Modal state
  const [showAIRevampModal, setShowAIRevampModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  // AI Revamp state
  const [isRevamping, setIsRevamping] = useState(false);
  const [revampSteps, setRevampSteps] = useState<string[]>([]);

  // Loading/saving state
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isRestoringState, setIsRestoringState] = useState(true);

  // Restore form state on mount (when coming back from recipients page)
  useEffect(() => {
    let editorInterval: NodeJS.Timeout | null = null;
    
    const savedState = localStorage.getItem('compose_temp_state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        console.log('Restoring compose state:', parsed);
        
        // Restore subject immediately
        if (parsed.subject) {
          setSubject(parsed.subject);
          console.log('✓ Subject restored:', parsed.subject);
        }
        
        // Restore workflow config immediately
        if (parsed.workflowConfig) {
          setWorkflowConfig(parsed.workflowConfig);
          console.log('✓ Workflow config restored');
        }
        
        // Restore selected integrations immediately
        if (parsed.selectedIntegrations) {
          setSelectedIntegrations(parsed.selectedIntegrations);
          console.log('✓ Selected integrations restored');
        }
        
        // Restore selected insights immediately
        if (parsed.selectedInsights) {
          setSelectedInsights(parsed.selectedInsights);
          console.log('✓ Selected insights restored');
        }
        
        // Restore content - use an interval to retry until editor is ready
        if (parsed.content) {
          setContent(parsed.content);
          console.log('✓ Content state set, waiting for editor...');
          
          let attempts = 0;
          const maxAttempts = 20; // Try for up to 2 seconds (20 * 100ms)
          
          editorInterval = setInterval(() => {
            attempts++;
            
            if (editorRef.current?.setContent) {
              try {
                const htmlContent = generateHTMLFromJSON(parsed.content);
                console.log('Generated HTML:', htmlContent.substring(0, 100) + '...');
                editorRef.current.setContent(htmlContent);
                console.log('✓ Editor content restored successfully!');
                if (editorInterval) clearInterval(editorInterval);
                editorInterval = null;
                
                // Clear the saved state only after successful restoration
                localStorage.removeItem('compose_temp_state');
              } catch (error) {
                console.error('Error setting editor content:', error);
                if (editorInterval) clearInterval(editorInterval);
                editorInterval = null;
                localStorage.removeItem('compose_temp_state');
              }
            } else if (attempts >= maxAttempts) {
              console.warn('Editor not ready after', maxAttempts, 'attempts');
              if (editorInterval) clearInterval(editorInterval);
              editorInterval = null;
              localStorage.removeItem('compose_temp_state');
            }
          }, 100);
        } else {
          // No content to restore, clear localStorage immediately
          localStorage.removeItem('compose_temp_state');
        }
      } catch (error) {
        console.error('Failed to restore compose state:', error);
        localStorage.removeItem('compose_temp_state');
      }
    }
    setIsRestoringState(false);
    
    // Cleanup function: clear interval if component unmounts
    return () => {
      if (editorInterval) {
        console.log('Cleaning up editor restoration interval on unmount');
        clearInterval(editorInterval);
      }
    };
  }, []);

  // Load integrations and past insights on mount
  useEffect(() => {
    loadIntegrations();
    loadPastInsights();
    
    const templateId = searchParams.get("template");
    const isBuiltIn = searchParams.get("builtin") !== "false";
    if (templateId) {
      applyTemplate(templateId, isBuiltIn);
    }
  }, [searchParams]);

  const loadIntegrations = async () => {
    try {
      const userIntegrations = await integrationsService.getUserIntegrations();
      // Transform to the format we need
      const transformed = userIntegrations
        .filter(i => i.integration_status === "active")
        .map(i => ({
          id: i.id,
          provider: i.provider,
          name: i.provider.charAt(0).toUpperCase() + i.provider.slice(1),
        }));
      setIntegrations(transformed);
      // Auto-select all integrations
      const autoSelect = transformed.map(i => i.id);
      setSelectedIntegrations(autoSelect);
    } catch (error) {
      console.error("Failed to load integrations:", error);
    }
  };

  const loadPastInsights = async () => {
    try {
      // Try the main insights endpoint first
      // API returns { success: true, data: { insights: [...] } }
      // Axios wraps in response.data, so we need response.data.data.insights
      const response = await apiService.get("/insights");
      console.log("Insights API response:", response.data);
      
      // Handle nested data structure: { success, data: { insights } }
      const insightsData = response.data?.data?.insights || response.data?.insights;
      
      if (insightsData && insightsData.length > 0) {
        setPastInsights(insightsData.slice(0, 10));
        return;
      }
      
      // Handle case where API returns array directly in data
      if (Array.isArray(response.data?.data) && response.data.data.length > 0) {
        setPastInsights(response.data.data.slice(0, 10));
        return;
      }
      
      // Fallback to dashboard endpoint
      const dashResponse = await apiService.get("/insights/dashboard");
      console.log("Dashboard insights response:", dashResponse.data);
      const dashInsights = dashResponse.data?.data?.insights || dashResponse.data?.insights;
      if (dashInsights && dashInsights.length > 0) {
        setPastInsights(dashInsights.slice(0, 10));
      }
    } catch (error) {
      console.error("Failed to load past insights:", error);
      // Try search endpoint as last resort
      try {
        const searchResponse = await apiService.get("/insights/search?limit=10");
        console.log("Search insights response:", searchResponse.data);
        const searchInsights = searchResponse.data?.data?.insights || searchResponse.data?.insights;
        if (searchInsights && searchInsights.length > 0) {
          setPastInsights(searchInsights.slice(0, 10));
        }
      } catch (searchError) {
        console.error("Failed to search insights:", searchError);
      }
    }
  };

  const applyTemplate = async (templateId: string, isBuiltIn = true) => {
    // Helper to set editor content with retry logic (editor might not be ready immediately)
    const setEditorContent = (content: string) => {
      if (editorRef.current?.setContent) {
        editorRef.current.setContent(content);
        return true;
      }
      // Retry with delay if editor not ready
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        attempts++;
        if (editorRef.current?.setContent) {
          editorRef.current.setContent(content);
          clearInterval(interval);
        } else if (attempts >= maxAttempts) {
          console.warn("Editor not ready for template content after", maxAttempts, "attempts");
          clearInterval(interval);
        }
      }, 100);
      return false;
    };

    if (isBuiltIn) {
      // Find template from built-in templates
      const template = builtInTemplates.find(t => t.id === templateId);
      if (template) {
        setSubject(template.subject);
        setEditorContent(template.content);
      }
    } else {
      // Load custom template from API
      try {
        const response = await apiService.getEmailTemplates();
        const template = response.data?.find((t: any) => t.id === templateId);
        if (template) {
          setSubject(template.subject);
          setEditorContent(template.content);
        }
      } catch (error) {
        console.error("Failed to load custom template:", error);
      }
    }
  };

  const handleToggleIntegration = (integrationId: string) => {
    setSelectedIntegrations((prev) =>
      prev.includes(integrationId)
        ? prev.filter((id) => id !== integrationId)
        : [...prev, integrationId]
    );
  };

  /**
   * Handle when a new integration is connected via the inline connector.
   * Refreshes the integrations list and auto-selects the newly connected integration.
   */
  const handleIntegrationConnected = useCallback(async (provider: string) => {
    console.log(`[ComposeUpdatePage] New integration connected: ${provider}`);

    // Reload integrations from backend
    try {
      const userIntegrations = await integrationsService.getUserIntegrations();
      const transformed = userIntegrations
        .filter(i => i.integration_status === "active")
        .map(i => ({
          id: i.id,
          provider: i.provider,
          name: i.provider.charAt(0).toUpperCase() + i.provider.slice(1),
        }));

      setIntegrations(transformed);

      // Find and auto-select the newly connected integration
      const newIntegration = transformed.find(i => i.provider === provider);
      if (newIntegration && !selectedIntegrations.includes(newIntegration.id)) {
        setSelectedIntegrations(prev => [...prev, newIntegration.id]);
        console.log(`[ComposeUpdatePage] Auto-selected new integration: ${newIntegration.id}`);
      }
    } catch (error) {
      console.error("Failed to refresh integrations after connection:", error);
    }
  }, [selectedIntegrations]);

  const handleAddInsightToEmail = useCallback(async (insight: DisplayInsight) => {
    let description = "";
    if (typeof insight.summary === "string") {
      description = insight.summary;
    } else if (insight.summary?.narrative) {
      description = insight.summary.narrative;
    } else if (insight.summary?.headline) {
      description = insight.summary.headline;
    }
    
    // Insert plain content
    const insightHtml = `<p><strong>${insight.title}</strong></p><p>${description}</p>`;
    
    if (editorRef.current?.insertAtCursor) {
      editorRef.current.insertAtCursor(insightHtml);
      
      // Check if this is a newly generated insight without a valid database ID
      let insightToTrack = insight;
      const hasValidDatabaseId = insight.id && 
        !insight.id.startsWith('insight_temp_') && 
        !insight.id.startsWith('temp_') &&
        !/_\d+$/.test(insight.id);
      
      console.log(`📎 Adding insight "${insight.title}" to email`);
      console.log(`   Current ID: ${insight.id}`);
      console.log(`   Has valid DB ID: ${hasValidDatabaseId}`);
      
      if (!hasValidDatabaseId) {
        console.log('🔄 Auto-saving insight to database before attaching:', insight.title);
        try {
          const savedInsight = await handleSaveInsight(insight);
          // Update the insight with the database ID
          insightToTrack = { ...insight, id: savedInsight.id };
          console.log('✅ Insight saved with ID:', insightToTrack.id);
        } catch (error) {
          console.error('⚠️ Failed to save insight, will use temporary ID:', error);
          // Continue anyway with the original insight
        }
      } else {
        console.log('✓ Insight already has valid database ID:', insight.id);
      }
      
      // Track this insight as selected (with database ID if saved)
      setSelectedInsights(prev => {
        // Avoid duplicates based on either the new ID or original ID
        const isDuplicate = prev.some(i => i.id === insightToTrack.id || i.id === insight.id);
        if (isDuplicate) {
          console.log('⚠️ Insight already in selection, skipping');
          return prev;
        }
        console.log('✅ Added insight to selection:', insightToTrack.id);
        return [...prev, insightToTrack];
      });
      
      // Apply highlight to newly added elements after a delay
      setTimeout(() => {
        // Try multiple selectors to find the editor
        const editorEl = document.querySelector('.su-compose-editor-content') || 
                         document.querySelector('.ProseMirror') ||
                         document.querySelector('.tiptap');
        
        console.log('Editor element found:', editorEl);
        
        if (editorEl) {
          // Get the last 2 paragraph elements
          const paragraphs = editorEl.querySelectorAll('p');
          console.log('Paragraphs found:', paragraphs.length);
          
          const lastTwo = Array.from(paragraphs).slice(-2);
          lastTwo.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.cssText = `
              background: linear-gradient(135deg, rgba(167, 139, 250, 0.4) 0%, rgba(139, 92, 246, 0.3) 100%) !important;
              box-shadow: 0 0 20px rgba(167, 139, 250, 0.6), inset 0 0 15px rgba(167, 139, 250, 0.3) !important;
              border-radius: 6px !important;
              padding: 10px 14px !important;
              margin: 6px 0 !important;
              transition: all 0.5s ease !important;
            `;
            htmlEl.setAttribute('data-highlighted', 'true');
            console.log('Applied highlight to:', htmlEl);
          });
          
          // Remove highlight after 10 seconds
          setTimeout(() => {
            const highlighted = document.querySelectorAll('[data-highlighted="true"]');
            highlighted.forEach((el) => {
              const htmlEl = el as HTMLElement;
              htmlEl.style.cssText = `
                background: transparent !important;
                box-shadow: none !important;
                padding: 0 !important;
                transition: all 1s ease !important;
              `;
              htmlEl.removeAttribute('data-highlighted');
            });
          }, 10000);
        }
      }, 100);
    }
  }, []);

  // Handle saving an insight to the database
  const handleSaveInsight = useCallback(async (insight: DisplayInsight) => {
    try {
      console.log('💾 Saving insight to database:', insight.title);
      // Save insight to database via API
      // API requires: type, category, title, question
      const response = await apiService.post("/insights", {
        title: insight.title,
        summary: insight.summary,
        data_sources: insight.data_sources || [],
        type: "comparison",
        category: "operational",
        question: `Insight: ${insight.title}`, // Required field
      });
      
      console.log('📥 API response:', response.data);
      
      // Return the saved insight with its database ID
      if (response.data?.data?.id) {
        console.log('✅ Got insight ID from response.data.data.id:', response.data.data.id);
        return { ...insight, id: response.data.data.id };
      } else if (response.data?.id) {
        console.log('✅ Got insight ID from response.data.id:', response.data.id);
        return { ...insight, id: response.data.id };
      }
      
      console.warn('⚠️ No ID found in API response, returning original insight');
      return insight;
    } catch (error) {
      console.error("❌ Failed to save insight:", error);
      throw error;
    }
  }, []);

  // Handle accepting a suggestion from SuggestionsSidebar
  const handleAcceptSuggestion = useCallback((suggestion: ContentSuggestion) => {
    if (suggestion.suggestedText && editorRef.current?.insertAtCursor) {
      // Insert the suggested text at cursor position
      editorRef.current.insertAtCursor(suggestion.suggestedText);

      // Apply highlight effect to show the inserted text
      setTimeout(() => {
        const editorEl = document.querySelector('.su-compose-editor-content') ||
                         document.querySelector('.ProseMirror') ||
                         document.querySelector('.tiptap');

        if (editorEl) {
          // Get the last paragraph element (where text was just inserted)
          const paragraphs = editorEl.querySelectorAll('p');
          const lastParagraph = paragraphs[paragraphs.length - 1] as HTMLElement;

          if (lastParagraph) {
            lastParagraph.style.cssText = `
              background: linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.2) 100%) !important;
              box-shadow: 0 0 15px rgba(16, 185, 129, 0.4) !important;
              border-radius: 6px !important;
              padding: 8px 12px !important;
              margin: 4px 0 !important;
              transition: all 0.5s ease !important;
            `;
            lastParagraph.setAttribute('data-suggestion-highlight', 'true');

            // Remove highlight after 5 seconds
            setTimeout(() => {
              const highlighted = document.querySelectorAll('[data-suggestion-highlight="true"]');
              highlighted.forEach((el) => {
                const htmlEl = el as HTMLElement;
                htmlEl.style.cssText = `
                  background: transparent !important;
                  box-shadow: none !important;
                  padding: 0 !important;
                  transition: all 1s ease !important;
                `;
                htmlEl.removeAttribute('data-suggestion-highlight');
              });
            }, 5000);
          }
        }
      }, 100);
    }

    console.log('✅ Accepted suggestion:', suggestion.title);
  }, []);

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      const draftData = {
        subject,
        content,
        workflow_config: workflowConfig,
        selected_integrations: selectedIntegrations,
      };

      if (draftId) {
        await apiService.updateDraft(draftId, draftData);
      } else {
        const response = await apiService.createDraft(draftData);
        if (response.data?.id) {
          setDraftId(response.data.id);
        }
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Check if form is valid (has subject and content)
  const isFormValid = () => {
    // Check subject
    if (!subject || subject.trim().length === 0) {
      return false;
    }
    
    // Check content - get text from editor to reliably detect empty content
    const currentText = editorRef.current?.getText() || "";

    // Content is invalid if there's no actual text
    if (!currentText.trim()) {
      return false;
    }
    
    return true;
  };

  const handleChooseRecipients = () => {
    // Save current state to localStorage before navigating
    const currentState = {
      subject,
      content,
      workflowConfig,
      selectedIntegrations,
      selectedInsights,
    };
    
    console.log('💾 Saving compose state before navigation:');
    console.log('  - Subject:', subject);
    console.log('  - Selected Integrations:', selectedIntegrations.length);
    console.log('  - Selected Insights:', selectedInsights.length);
    console.log('  - Insight IDs:', selectedInsights.map(i => ({ id: i.id, title: i.title })));
    
    localStorage.setItem('compose_temp_state', JSON.stringify(currentState));
    
    // Navigate to recipients page with subject
    const params = new URLSearchParams();
    if (subject) {
      params.set("subject", subject);
    }
    const queryString = params.toString();
    navigate(`/updates/recipients${queryString ? `?${queryString}` : ""}`);
  };

  const handleTemplateSelect = (templateId: string, isBuiltIn: boolean) => {
    applyTemplate(templateId, isBuiltIn);
    setShowTemplateModal(false);
  };

  const handleDraftSelect = (draft: Draft) => {
    // Populate subject
    if (draft.subject) {
      setSubject(draft.subject);
    }
    
    // Populate editor content - TipTap can accept JSON directly
    if (draft.content && editorRef.current?.setContent) {
      // Convert JSON content to HTML for the editor
      // Since content is stored as TipTap JSON, we need to generate HTML from it
      // For now, we'll use a simple approach - the editor can handle JSON
      try {
        // If content is already in JSON format from TipTap
        const htmlContent = generateHTMLFromJSON(draft.content);
        editorRef.current.setContent(htmlContent);
        setContent(draft.content);
      } catch (error) {
        console.error("Failed to set draft content:", error);
      }
    }
    
    // Optionally restore workflow config
    if (draft.workflow_config) {
      setWorkflowConfig(draft.workflow_config);
    }
    
    // Optionally restore selected integrations
    if (draft.selected_integrations && Array.isArray(draft.selected_integrations)) {
      setSelectedIntegrations(draft.selected_integrations);
    }
    
    // Set current draft ID for updating
    setDraftId(draft.id);
    
    // Close modal
    setShowDraftModal(false);
  };

  const handleAIRevamp = async (params: {
    instructions: string;
    additionalContext?: string;
    direction?: string;
    documents?: any[];
  }) => {
    // Get current content from editor
    const currentHtml = editorRef.current?.getHTML() || "";
    const currentText = editorRef.current?.getText() || "";

    if (!currentText.trim()) {
      alert("Please write some content in the email before using AI Revamp.");
      return;
    }

    setIsRevamping(true);

    // Chain of thought steps - show ALL immediately for sequential animation
    const steps = [
      "Analyzing current email content...",
      "Understanding improvement instructions...",
      params.documents && params.documents.length > 0
        ? "Processing uploaded documents..."
        : "Gathering context...",
      "Generating enhanced version with Claude...",
      "Finalizing revamped email...",
    ];

    // Show all steps at once so checkmarks can animate sequentially
    setRevampSteps([...steps]);
    const animationStartTime = Date.now();

    try {
      // Use new API with document support if documents provided
      const response = params.documents && params.documents.length > 0
        ? await apiService.revampEmailWithDocuments({
            content: currentHtml,
            instructions: params.instructions,
            additionalContext: params.additionalContext,
            direction: params.direction,
            documents: params.documents,
            subject,
          })
        : await apiService.revampEmail({
            content: currentHtml,
            instructions: params.instructions,
            subject,
          });

      // Calculate how long we need to wait for animation to complete
      // 5 steps × 3 seconds = 15 seconds total, plus 1 second buffer
      const totalAnimationTime = 16000;
      const elapsed = Date.now() - animationStartTime;
      const remainingTime = Math.max(0, totalAnimationTime - elapsed);
      
      // Wait for remaining animation time
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.data) {
        // Update subject if provided
        if (response.data.subject) {
          setSubject(response.data.subject);
        }

        // Get content from either response format (old: content, new: htmlContent)
        const newContent =
          "content" in response.data
            ? response.data.content
            : "htmlContent" in response.data
              ? response.data.htmlContent
              : null;

        // Apply revamped content to editor with highlight effect
        if (newContent && editorRef.current?.setContent) {
          editorRef.current.setContent(newContent);
          
          // Apply highlight to ALL content (since it's all new from revamp)
          setTimeout(() => {
            const editorEl = document.querySelector('.su-compose-editor-content') || 
                             document.querySelector('.ProseMirror') ||
                             document.querySelector('.tiptap');
            
            console.log('Revamp - Editor element found:', editorEl);
            
            if (editorEl) {
              // Highlight all text elements
              const allElements = editorEl.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote');
              console.log('Revamp - Elements found:', allElements.length);
              
              allElements.forEach((el) => {
                const htmlEl = el as HTMLElement;
                htmlEl.style.cssText = `
                  background: linear-gradient(135deg, rgba(167, 139, 250, 0.4) 0%, rgba(139, 92, 246, 0.3) 100%) !important;
                  box-shadow: 0 0 20px rgba(167, 139, 250, 0.6), inset 0 0 15px rgba(167, 139, 250, 0.3) !important;
                  border-radius: 6px !important;
                  padding: 10px 14px !important;
                  margin: 6px 0 !important;
                  transition: all 0.5s ease !important;
                `;
                htmlEl.setAttribute('data-highlighted', 'true');
              });
              
              // Remove highlight after 10 seconds
              setTimeout(() => {
                const highlighted = document.querySelectorAll('[data-highlighted="true"]');
                highlighted.forEach((el) => {
                  const htmlEl = el as HTMLElement;
                  htmlEl.style.cssText = `
                    background: transparent !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    transition: all 1s ease !important;
                  `;
                  htmlEl.removeAttribute('data-highlighted');
                });
              }, 10000);
            }
          }, 100);
        }
        
        // Close modal after success
        setShowAIRevampModal(false);
      }
    } catch (error: any) {
      console.error("Failed to revamp email:", error);
      const errorMsg = error?.response?.data?.error || "Failed to revamp email. Please try again.";
      setRevampSteps(prev => [...prev, `❌ Error: ${errorMsg}`]);
    } finally {
      setIsRevamping(false);
    }
  };

  return (
    <div className="max-w-[90rem] mx-auto py-8 px-6 lg:px-8 xl:px-12">
      {/* Page Header - Reusing from common */}
      <PageHeader
        title="Compose Update"
        subtitle="Create and customize your investor update"
        action={
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="su-btn-secondary"
              onClick={() => setShowDraftModal(true)}
            >
              <FileTextIcon className="w-4 h-4" />
              <span>FROM DRAFT</span>
            </button>
            <button
              className={`su-btn-secondary ${showConfigPanel ? "su-btn-active" : ""}`}
              onClick={() => setShowConfigPanel(!showConfigPanel)}
            >
              <SettingsIcon className="w-4 h-4" />
              <span>CONFIGURE WORKFLOW</span>
            </button>
          </div>
        }
      />

      {/* Workflow Configuration Panel (collapsible) */}
      {showConfigPanel && (
        <WorkflowConfigPanel
          config={workflowConfig}
          onChange={setWorkflowConfig}
          onClose={() => setShowConfigPanel(false)}
        />
      )}

      {/* Main Content - Two Column Grid */}
      <div className="su-compose-grid">
        {/* Left Column - Editor sections */}
        <div className="su-compose-left-column">
          <ComposeEditor
            ref={editorRef}
            subject={subject}
            onSubjectChange={setSubject}
            content={content}
            onContentChange={setContent}
            onAIRevamp={() => setShowAIRevampModal(true)}
            onUseTemplate={() => setShowTemplateModal(true)}
          />

          {/* Action Buttons - NOT in a section */}
          <div className="su-compose-actions">
            <button
              className="su-btn-secondary"
              onClick={handleSaveDraft}
              disabled={isSavingDraft}
            >
              {isSavingDraft ? "SAVING..." : "SAVE DRAFT"}
            </button>
            <button
              className="su-btn-primary"
              onClick={handleChooseRecipients}
              disabled={!isFormValid()}
            >
              <span>NEXT: CHOOSE RECIPIENTS</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right Column - Insights and Suggestions */}
        <div className="su-compose-right-column">
          <InsightsSidebar
            integrations={integrations}
            selectedIntegrations={selectedIntegrations}
            onToggleIntegration={handleToggleIntegration}
            generatedInsights={generatedInsights}
            pastInsights={pastInsights}
            onAddToEmail={handleAddInsightToEmail}
            onSaveInsight={handleSaveInsight}
            isGenerating={isGeneratingInsights}
            setIsGenerating={setIsGeneratingInsights}
            setGeneratedInsights={setGeneratedInsights}
            setPastInsights={setPastInsights}
            onIntegrationConnected={handleIntegrationConnected}
          />

          {/* AI Suggestions Panel */}
          <SuggestionsSidebar
            content={editorRef.current?.getHTML() || ""}
            subject={subject}
            integrationIds={selectedIntegrations}
            onAcceptSuggestion={handleAcceptSuggestion}
          />
        </div>
      </div>

      {/* Modals */}
      {showAIRevampModal && (
        <AIRevampModal
          onClose={() => !isRevamping && setShowAIRevampModal(false)}
          onRevamp={handleAIRevamp}
          isRevamping={isRevamping}
          revampSteps={revampSteps}
        />
      )}

      {showTemplateModal && (
        <ChooseTemplateModal
          onClose={() => setShowTemplateModal(false)}
          onSelect={handleTemplateSelect}
        />
      )}

      {showDraftModal && (
        <ChooseDraftModal
          onClose={() => setShowDraftModal(false)}
          onSelect={handleDraftSelect}
        />
      )}
    </div>
  );
};

export default ComposeUpdatePage;
